import { useTranslator } from "@eo-locale/react";

import dayjs from "dayjs";
import { useRouter } from "next/router";
import { ReactNode, useState } from "react";

import { getVotingStatus } from "@/helpers/ideas";
import { useAppSelector } from "@/redux/store/hooks";
import { HTMLWrapper } from "@/storybook-components";
import useUser from "@/hooks/useUser";
import Alerts from "@/components/alerts";

const AllAlerts = ({
  showVotingAlert = true
}: {
  showVotingAlert?: boolean;
}): JSX.Element => {
  const { asPath } = useRouter();
  const { translate } = useTranslator();
  const { isUserAdmin, isRegisteredUserLoggedIn } = useUser();
  const { state: projectState, visibilityMode } = useAppSelector(
    (state) => state.project.project
  );
  const { brainstormer } = useAppSelector((state) => state?.brainstormers);
  const {
    state: brainStormerState,
    archivalReasonMessage,
    votingStartAt,
    votingEndAt,
    disableVoting
  } = brainstormer;

  let alert = {} as {
    title?: string;
    description: string | ReactNode;
    status: "warning" | "success" | "error" | "info";
  };

  switch (Boolean(brainStormerState)) {
    case brainStormerState === "archived" || projectState === "archived":
      alert = {
        status: "info",
        description:
          archivalReasonMessage || translate("brainstormers.boardArchived")
      };
      break;
    case brainStormerState === "published" &&
      visibilityMode === "Panelled" &&
      isRegisteredUserLoggedIn &&
      !isUserAdmin:
      alert = {
        status: "info",
        description: translate("brainstormers.panelledListTitle")
      };
      break;
    case brainStormerState === "published" &&
      visibilityMode === "Panelled" &&
      !isRegisteredUserLoggedIn:
      alert = {
        status: "info",
        title: translate("brainstormers.panelledListTitle"),
        description: (
          <HTMLWrapper
            html={translate("brainstormers.panelledListDesc", {
              loginRoute: `/login?next=${asPath}`,
              target: "_parent"
            })}
          />
        )
      };
      break;
    case disableVoting:
      break;
    case showVotingAlert &&
      brainStormerState === "published" &&
      getVotingStatus(votingStartAt, votingEndAt) === "ended":
      alert = {
        status: "info",
        title: translate("brainstormers.votingClosed"),
        description: translate("brainstormers.thankyouForParticipation")
      };
      break;
    case showVotingAlert &&
      brainStormerState === "published" &&
      getVotingStatus(votingStartAt, votingEndAt) === "not started":
      alert = {
        status: "info",
        title: translate("brainstormers.haveYourSayToday"),
        description: translate("brainstormers.postContributions", {
          date: dayjs(votingStartAt).format("DD/MM/YYYY")
        })
      };
      break;
    case showVotingAlert &&
      brainStormerState === "published" &&
      getVotingStatus(votingStartAt, votingEndAt) === "active":
      alert = {
        status: "info",
        title: translate("brainstormers.thankyouForContribution"),
        description: translate("brainstormers.voteForYourFavourites", {
          date: dayjs(votingEndAt).format("DD/MM/YYYY")
        })
      };
      break;
  }
  const [showAlert, setShowAlert] = useState(Object.keys(alert).length > 0);
  const { status, title, description } = alert;

  if (showAlert)
    return (
      <Alerts
        mb={12}
        title={title}
        status={status}
        description={description}
        onClose={() => setShowAlert(!showAlert)}
      />
    );

  return null;
};

export default AllAlerts;
